import React, { useState, useEffect } from 'react';
import { useThemeMode } from '../../ThemeContext';
import { dataAPI } from '../../helpers/api_service';
import { formatDateToAPI } from '../../helpers/UtilsFunctions';
import CloseIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/Warning';
import { TextField, FormControl, Select, MenuItem } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { enGB } from 'date-fns/locale';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import AppIconBlue from '../../assets/AppIconBlue.svg';
import '../../styles/loading-animations.css';

const ProjectForm = ({ isOpen, onClose, onSubmit, project = null, isEditMode = false }) => {
  const { mode } = useThemeMode();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_date: null,
    end_date: null,
    status: 'planned'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [quillKey, setQuillKey] = useState(0);
  const [showFormLoading, setShowFormLoading] = useState(false);
  const [showValidation, setShowValidation] = useState(false);

  // Helper function to convert plain text to HTML if needed
  const convertDescriptionToHtml = (description) => {
    if (!description) return '';
    // If it's already HTML (contains tags), return as is
    if (description.includes('<') && description.includes('>')) {
      return description;
    }
    // Convert plain text to HTML with line breaks
    return description.replace(/\n/g, '<br>');
  };

  // Update form data when project prop changes (for edit mode)
  useEffect(() => {
    if (!isOpen) return; // Don't initialize if form is closed
    
    setIsDataLoaded(false); // Reset loading state first
    
    // Use setTimeout to ensure state updates are processed
    setTimeout(() => {
      if (project && isEditMode) {
        setFormData({
          title: project.title || '',
          description: convertDescriptionToHtml(project.description || ''),
          start_date: project.start_date ? new Date(project.start_date) : null,
          end_date: project.end_date ? new Date(project.end_date) : null,
          status: project.status || 'planned'
        });
      } else {
        // Reset form for create mode
        setFormData({
          title: '',
          description: '',
          start_date: null,
          end_date: null,
          status: 'planned'
        });
      }
      setQuillKey(prev => prev + 1);
      setIsDataLoaded(true);
    }, 10);
  }, [isOpen, project, isEditMode]);

  // Custom theme for Material-UI components to match current styling
  const customTheme = createTheme({
    palette: {
      mode: mode,
      primary: {
        main: '#26C3C0',
      },
      background: {
        default: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
        paper: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
      },
      text: {
        primary: mode === 'dark' ? '#FFFFFF' : '#222222',
        secondary: mode === 'dark' ? '#666' : '#999',
      },
    },
    components: {
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: '6px',
              fontFamily: "'Urbanist', Arial, sans-serif",
              fontSize: '14px',
              '& fieldset': {
                borderColor: mode === 'dark' ? '#444' : '#ddd',
                transition: 'border-color 0.2s ease',
              },
              '&:hover fieldset': {
                borderColor: '#26C3C0',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#26C3C0',
              },
            },
            '& .MuiInputLabel-root': {
              color: mode === 'dark' ? '#666' : '#999',
              fontFamily: "'Urbanist', Arial, sans-serif",
              fontSize: '14px',
              '&.Mui-focused': {
                color: '#26C3C0',
              },
            },
          },
        },
      },
      MuiDatePicker: {
        styleOverrides: {
          root: {
            '& .MuiPickersDay-root': {
              fontFamily: "'Urbanist', Arial, sans-serif",
              '&.Mui-selected': {
                backgroundColor: '#26C3C0',
                '&:hover': {
                  backgroundColor: '#1f9e9b',
                },
              },
            },
          },
        },
      },
    },
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Validation for dates
  const validateDates = () => {
    // Only validate if project title has a value
    if (!formData.title || formData.title.trim() === '') {
      return null;
    }
    
    const currentYear = new Date().getFullYear();
    
    // Check if start date is provided
    if (!formData.start_date) {
      return 'Project start date is required';
    }
    
    // Check if end date is provided
    if (!formData.end_date) {
      return 'Project end date is required';
    }
    
    // Check if start date is in the past year
    if (formData.start_date) {
      const startYear = formData.start_date.getFullYear();
      
      if (startYear < currentYear) {
        return 'Project start date cannot be in the past year';
      }
    }
    
    // Check if end date is in the past year
    if (formData.end_date) {
      const endYear = formData.end_date.getFullYear();
      
      if (endYear < currentYear) {
        return 'Project end date cannot be in the past year';
      }
    }
    
    // Check if start date is later than end date
    if (formData.start_date && formData.end_date) {
      if (formData.start_date > formData.end_date) {
        return 'Start date cannot be later than end date';
      }
    }
    return null;
  };
  
  const dateValidationError = validateDates();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validation: title is required
    if (!formData.title.trim()) {
      setError('Project title is required');
      return;
    }

    // Check for date validation errors before proceeding
    if (dateValidationError) {
      return; // Stop submission if there are date validation errors
    }

    setIsSubmitting(true);
    setShowFormLoading(true);
    setShowValidation(true);
    
    try {
      // Prepare data for API (convert Date objects to YYYY-MM-DD format)
      const apiData = {
        title: formData.title,
        description: formData.description,
        start_date: formatDateToAPI(formData.start_date) || null,
        end_date: formatDateToAPI(formData.end_date) || null,
        status: formData.status
      };
      
      let response;
      if (isEditMode && project) {
        // Call edit API
        response = await dataAPI.editProject(project.id, apiData, { showLoading: false });
        console.log('Project update successful:', response);
      } else {
        // Call create API
        response = await dataAPI.createProject(apiData, { showLoading: false });
        console.log('Project creation successful:', response);
      }
      
      // Success - call the parent onSubmit callback
      onSubmit(response);
      
      // Reset form
      setFormData({
        title: '',
        description: '',
        start_date: null,
        end_date: null,
        status: 'planned'
      });
      setIsDataLoaded(false);
      
      onClose();
    } catch (error) {
      console.error(isEditMode ? 'Update project error:' : 'Create project error:', error);
      setError(error.message || `Failed to ${isEditMode ? 'update' : 'create'} project. Please try again.`);
    } finally {
      setIsSubmitting(false);
      setShowFormLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      title: '',
      description: '',
      start_date: '',
      end_date: '',
      status: 'planned'
    });
    setError('');
    setIsDataLoaded(false);
    setShowFormLoading(false);
    setShowValidation(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ThemeProvider theme={customTheme}>
      <>
        <style>
          {`
            /* ReactQuill custom styles */
            .ql-editor {
              background-color: ${mode === 'dark' ? '#2a2a2a' : '#ffffff'} !important;
              color: ${mode === 'dark' ? '#ffffff' : '#000000'} !important;
              font-family: 'Urbanist', Arial, sans-serif !important;
              font-size: 14px !important;
              line-height: 1.6 !important;
              min-height: 80px !important;
              max-height: 120px !important;
              overflow-y: auto !important;
            }
            .ql-editor * {
              font-family: 'Urbanist', Arial, sans-serif !important;
            }
            .ql-editor p, .ql-editor div, .ql-editor span, .ql-editor strong, .ql-editor em, .ql-editor ul, .ql-editor ol, .ql-editor li {
              font-family: 'Urbanist', Arial, sans-serif !important;
              font-size: 14px !important;
            }
            /* More aggressive font overrides for ReactQuill */
            .ql-editor[contenteditable="true"] {
              font-family: 'Urbanist', Arial, sans-serif !important;
            }
            .ql-editor[contenteditable="true"] * {
              font-family: 'Urbanist', Arial, sans-serif !important;
            }
            .ql-editor.ql-blank {
              font-family: 'Urbanist', Arial, sans-serif !important;
            }
            .ql-editor.ql-blank::before {
              font-family: 'Urbanist', Arial, sans-serif !important;
            }
            .ql-editor::-webkit-scrollbar {
              width: 6px !important;
            }
            .ql-editor::-webkit-scrollbar-track {
              background: ${mode === 'dark' ? '#1a1a1a' : '#f1f1f1'} !important;
              border-radius: 3px !important;
            }
            .ql-editor::-webkit-scrollbar-thumb {
              background: ${mode === 'dark' ? '#555' : '#c1c1c1'} !important;
              border-radius: 3px !important;
            }
            .ql-editor::-webkit-scrollbar-thumb:hover {
              background: ${mode === 'dark' ? '#777' : '#a1a1a1'} !important;
            }
            .ql-toolbar {
              background-color: ${mode === 'dark' ? '#333' : '#f8f9fa'} !important;
              border-color: ${mode === 'dark' ? '#444' : '#ddd'} !important;
              border-radius: 8px 8px 0 0 !important;
              padding: 8px 12px !important;
            }
            .ql-container {
              border-color: ${mode === 'dark' ? '#444' : '#ddd'} !important;
              border-radius: 0 0 8px 8px !important;
            }
            .ql-toolbar .ql-stroke {
              stroke: ${mode === 'dark' ? '#ffffff' : '#444'} !important;
            }
            .ql-toolbar .ql-fill {
              fill: ${mode === 'dark' ? '#ffffff' : '#444'} !important;
            }
            .ql-toolbar button {
              border: 1px solid ${mode === 'dark' ? '#555' : '#ccc'} !important;
              border-radius: 6px !important;
              background-color: ${mode === 'dark' ? '#444' : '#ffffff'} !important;
              margin: 0 2px !important;
              padding: 6px 8px !important;
              height: 32px !important;
              min-width: 32px !important;
              transition: all 0.2s ease !important;
              box-shadow: 0 1px 3px rgba(0,0,0,0.1) !important;
            }
            .ql-toolbar button:hover {
              background-color: ${mode === 'dark' ? '#555' : '#f0f0f0'} !important;
              border-color: #26C3C0 !important;
              box-shadow: 0 2px 6px rgba(38, 195, 192, 0.2) !important;
            }
            .ql-toolbar button.ql-active {
              background-color: #26C3C0 !important;
              border-color: #26C3C0 !important;
              color: white !important;
              box-shadow: 0 2px 8px rgba(38, 195, 192, 0.3) !important;
            }
            .ql-toolbar button.ql-active .ql-stroke {
              stroke: white !important;
            }
            .ql-toolbar button.ql-active .ql-fill {
              fill: white !important;
            }
            
            /* ReactQuill placeholder styling */
            .ql-editor.ql-blank::before {
              color: ${mode === 'dark' ? '#B0B0B0' : '#999999'} !important;
              font-style: normal !important;
              font-family: 'Urbanist', Arial, sans-serif !important;
              font-size: 14px !important;
            }
            
            /* Hide scrollbar for project form modal */
            .project-form-modal {
              scrollbar-width: none !important; /* Firefox */
              -ms-overflow-style: none !important; /* Internet Explorer 10+ */
            }
            .project-form-modal::-webkit-scrollbar {
              display: none !important; /* WebKit */
            }
          `}
        </style>
        {/* Backdrop */}
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000
          }}
          onClick={handleClose}
        />
        
        {/* Modal */}
        <div 
          className="project-form-modal"
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            width: '420px',
            height: 'calc(100vh - 40px)',
            backgroundColor: mode === 'dark' ? '#1a1a1a' : '#ffffff',
            boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.3)',
            zIndex: 1001,
            overflowY: 'auto',
          borderRadius: '12px'
        }}>
          {/* Header */}
          <div
            className="project-form-header"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px',
              borderBottom: `1px solid ${mode === 'dark' ? '#333' : '#e0e0e0'}`
            }}
          >
            <h2 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: mode === 'dark' ? '#FFFFFF' : '#222222',
              margin: 0,
              fontFamily: "'Urbanist', Arial, sans-serif"
            }}>
              {isEditMode ? 'Edit Project' : 'Create Project'}
            </h2>
            <button
              onClick={handleClose}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '6px',
                borderRadius: '4px',
                color: mode === 'dark' ? '#FFFFFF' : '#666666',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <CloseIcon style={{ fontSize: '20px' }} />
            </button>
          </div>

          {/* Form Loading Overlay */}
          {showFormLoading && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1003,
              backgroundColor: mode === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(4px)',
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '12px'
            }}>
              {/* Fill Animation Loading Indicator */}
              <div className="fill-animation-container">
                {/* Background container */}
                <div className="fill-animation-background" />
                
                {/* Progress fill animation */}
                <div className="fill-animation-progress" />
                
                {/* AppIconBlue with pulse animation */}
                <img 
                  src={AppIconBlue} 
                  alt="Loading" 
                  className={`fill-animation-icon pulse-animation-icon`}
                />
              </div>
            </div>
          )}

          {/* Form Content */}
          <form onSubmit={handleSubmit} style={{ padding: '20px' }}>
            {/* Error Message */}
            {error && (
              <div style={{
                marginBottom: '16px',
                backgroundColor: '#1a0000',
                border: '1px solid #ff4444',
                borderRadius: '8px',
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                boxShadow: '0 2px 8px rgba(255, 68, 68, 0.2)'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <WarningIcon style={{
                    color: '#ff4444',
                    fontSize: '20px'
                  }} />
                  <span style={{
                    color: '#ffffff',
                    fontSize: '14px',
                    fontFamily: "'Urbanist', Arial, sans-serif",
                    lineHeight: '1.4'
                  }}>
                    {error}
                  </span>
                </div>
              </div>
            )}

            {/* Project Name */}
            <div style={{ marginBottom: '16px' }}>
              <TextField
                size="small"
                required
                label="Project Name"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                disabled={isSubmitting}
                error={showValidation && !formData.title.trim()}
                helperText={showValidation && !formData.title.trim() ? 'Project name is required' : ''}
                style={{
                  width: '100%',
                  backgroundColor: mode === 'dark' ? '#1a1a1a' : '#ffffff',
                  color: mode === 'dark' ? '#FFFFFF' : '#222222'
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    transition: 'all 0.2s ease',
                    '& fieldset': {
                      borderColor: mode === 'dark' ? '#444' : '#ddd',
                    },
                    '&:hover fieldset': {
                      borderColor: '#26C3C0',
                      boxShadow: '0 4px 8px rgba(38,195,192,0.2)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#26C3C0',
                      boxShadow: '0 0 0 2px rgba(38,195,192,0.1)',
                    },
                    '&.Mui-error fieldset': {
                      borderColor: '#f44336',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: mode === 'dark' ? '#999' : '#666',
                    fontSize: '14px',
                  },
                  '& .MuiFormHelperText-root': {
                    color: '#f44336',
                    fontSize: '12px',
                  },
                }}
              />
            </div>

            {/* Date Fields - Side by Side */}
            <div className="project-form-dates-row" style={{ marginBottom: '16px', display: 'flex', gap: '12px' }}>
              {/* Start Date */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
                  <DatePicker
                    value={formData.start_date}
                    onChange={(newValue) => {
                      handleInputChange('start_date', newValue);
                    }}
                    label="Start Date *"
                    format="dd-MM-yyyy"
                  />
                </LocalizationProvider>
              </div>

              {/* Due Date */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
                  <DatePicker
                    value={formData.end_date}
                    onChange={(newValue) => {
                      handleInputChange('end_date', newValue);
                    }}
                    label="Due Date *"
                    format="dd-MM-yyyy"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '6px',
                            backgroundColor: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
                            color: mode === 'dark' ? '#FFFFFF' : '#222222',
                            fontSize: '14px',
                            fontFamily: "'Urbanist', Arial, sans-serif",
                            '& fieldset': {
                              borderColor: formData.end_date ? '#26C3C0' : (mode === 'dark' ? '#444' : '#ddd'),
                              transition: 'border-color 0.2s ease',
                            },
                            '&:hover fieldset': {
                              borderColor: '#26C3C0',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#26C3C0',
                            },
                          },
                          '& .MuiInputBase-input': {
                            padding: '12px 16px',
                            height: 'auto',
                            lineHeight: 'normal',
                            backgroundColor: 'transparent',
                          },
                          '& .MuiInputBase-root': {
                            backgroundColor: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
                          },
                          backgroundColor: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
                        }
                      }
                    }}
                  />
                </LocalizationProvider>
              </div>
            </div>

            {/* Date Validation Error */}
            {dateValidationError && (
              <div style={{
                backgroundColor: mode === 'dark' ? '#1a0000' : '#ffebee',
                border: '1px solid #ff4444',
                borderRadius: '8px',
                padding: '12px 16px',
                marginBottom: '24px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                boxShadow: '0 2px 8px rgba(255, 68, 68, 0.2)'
              }}>
                <WarningIcon style={{
                  color: '#ff4444',
                  fontSize: '20px'
                }} />
                <span style={{
                  color: mode === 'dark' ? '#ffffff' : '#d32f2f',
                  fontSize: '14px',
                  fontFamily: "'Urbanist', Arial, sans-serif",
                  lineHeight: '1.4'
                }}>
                  {dateValidationError}
                </span>
              </div>
            )}

            {/* Description */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontFamily: "'Urbanist', Arial, sans-serif",
                color: mode === 'dark' ? '#FFFFFF' : '#222222',
                fontWeight: '500'
              }}>
                Project Description
              </label>
              {isDataLoaded ? (
                <div style={{ 
                  backgroundColor: mode === 'dark' ? '#2a2a2a' : '#ffffff',
                  borderRadius: '8px',
                  border: `1px solid ${mode === 'dark' ? '#444' : '#ddd'}`,
                  maxHeight: '150px' // 5 rows * 30px per row
                }}>
                  <ReactQuill
                    key={`quill-${quillKey}`}
                    value={formData.description || ''}
                    onChange={(value) => handleInputChange('description', value)}
                    placeholder="Enter project description..."
                    modules={{
                      toolbar: [
                        ['bold', 'italic'],
                        [{ 'list': 'ordered'}, { 'list': 'bullet' }]
                      ]
                    }}
                    formats={['bold', 'italic', 'list', 'bullet']}
                    theme="snow"
                    style={{
                      backgroundColor: 'transparent',
                      border: 'none',
                      maxHeight: '150px',
                      fontFamily: "'Urbanist', Arial, sans-serif"
                    }}
                    sx={{
                      '& .ql-editor': {
                        fontFamily: 'Urbanist, Arial, sans-serif !important',
                        fontSize: '14px !important',
                        color: mode === 'dark' ? '#FFFFFF' : '#000000'
                      },
                      '& .ql-editor *': {
                        fontFamily: 'Urbanist, Arial, sans-serif !important'
                      },
                      '& .ql-editor p, & .ql-editor div, & .ql-editor span, & .ql-editor strong, & .ql-editor em, & .ql-editor ul, & .ql-editor ol, & .ql-editor li': {
                        fontFamily: 'Urbanist, Arial, sans-serif !important',
                        fontSize: '14px !important'
                      }
                    }}
                  />
                </div>
              ) : (
                <div style={{
                  minHeight: '80px',
                  backgroundColor: mode === 'dark' ? '#2a2a2a' : '#ffffff',
                  border: `1px solid ${mode === 'dark' ? '#444' : '#ddd'}`,
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: mode === 'dark' ? '#666' : '#999',
                  fontSize: '14px',
                  fontFamily: "'Urbanist', Arial, sans-serif"
                }}>
                  Loading editor...
                </div>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting || !formData.title.trim()}
              style={{
                width: '100%',
                padding: '14px 24px',
                backgroundColor: formData.title.trim() && !isSubmitting ? '#26C3C0' : (mode === 'dark' ? '#444' : '#666'),
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '500',
                cursor: formData.title.trim() && !isSubmitting ? 'pointer' : 'not-allowed',
                fontFamily: "'Urbanist', Arial, sans-serif",
                transition: 'background-color 0.2s ease',
                opacity: isSubmitting ? 0.7 : 1
              }}
              onMouseEnter={(e) => {
                if (formData.title.trim() && !isSubmitting) {
                  e.target.style.backgroundColor = '#22B0AD';
                }
              }}
              onMouseLeave={(e) => {
                if (formData.title.trim() && !isSubmitting) {
                  e.target.style.backgroundColor = '#26C3C0';
                }
              }}
            >
              {isSubmitting ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Project' : 'Create Project')}
            </button>
          </form>
        </div>
      </>
    </ThemeProvider>
  );
};

export default ProjectForm;